import { IKeyFact, IPolicyKeyFactMetaData, getValueAtPropertyPath } from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { charmGray, grayIconFill } from '../../../styles/colors';
import { TextInput } from '../../TextInput';
import { SimpleTooltip, TooltipProvider } from '../../Tooltip';
import { RoundAddIcon } from '../../svgs/icons/RoundAddIcon';
import { PrivateVisibilityIcon } from '../../svgs/icons/PrivateVisibilityIcon';
import { XIcon } from '../../svgs/icons/XIcon';
import { styleSheet } from './styles';

interface IProps<T = any> {
	addButtonContent?: React.ReactNode;
	className?: string;
	items?: T[];
	itemValueKeyPath: string;
	listItemClassName?: string;
	onAddButtonClicked?(): void;
	onItemValueChanged?(item: T, index: number, value: string): void;
	onRemoveButtonClicked?(item: T, index: number): void;
	onRenderItem?(item: T, index: number): React.ReactNode;
	baseItemKey: string;
	/** Optional function to determine if an item can be removed */
	canRemoveItem?(item: T, index: number): boolean;
}

interface IState<T = any> {
	items?: T[];
}

class _EditableEntityInfoBulletList<T extends object = any> extends React.Component<IProps<T>, IState<T>> {
	public readonly state: IState<T> = {
		items: [],
	};

	public UNSAFE_componentWillMount() {
		const nextState = this.getNextStateWithProps(this.props);
		if (nextState) {
			this.setState(nextState);
		}
	}

	public UNSAFE_componentWillReceiveProps(nextProps: IProps<T>) {
		const nextState = this.getNextStateWithProps(nextProps);
		if (nextState) {
			this.setState(nextState);
		}
	}

	public render() {
		const {
			className,
			onAddButtonClicked,
			itemValueKeyPath,
			addButtonContent,
			listItemClassName,
			onRenderItem,
			baseItemKey,
			canRemoveItem,
		} = this.props;
		const { items } = this.state;
		return (
			<TooltipProvider>
				<div className={`${css(styleSheet.container)} editable-entity-info-bullet-list ${className || ''}`}>
					{items.map((x, i) => {
						const isProtectedKeyFact = !!((x as IKeyFact).source?.additionalMetadata as IPolicyKeyFactMetaData)
							?.policyId;
						// Use canRemoveItem prop if provided, otherwise fall back to policyId check
						const canRemove = canRemoveItem ? canRemoveItem(x, i) : !isProtectedKeyFact;

						const value = itemValueKeyPath ? getValueAtPropertyPath(x, itemValueKeyPath) : null;
						// Get source information for tooltip
						const keyFact = x as IKeyFact;
						const sourceName = keyFact?.source?.source;
						const tooltipMessage = sourceName
							? `This information is from ${sourceName} and cannot be edited or deleted.`
							: 'This information cannot be edited or deleted.';

						return (
							<div className={`${css(styleSheet.listItem)} ${listItemClassName || ''}`} key={`${baseItemKey}-${i}`}>
								{canRemove ? (
									<button className={css(styleSheet.listItemIcon)} onClick={this.onRemoveButtonClicked(i)}>
										<XIcon color={charmGray} height={12} width={12} />
									</button>
								) : (
									<SimpleTooltip title={tooltipMessage}>
										<div className={css(styleSheet.listItemIcon)}>
											<PrivateVisibilityIcon fillColor={grayIconFill} />
										</div>
									</SimpleTooltip>
								)}
								{onRenderItem ? (
									onRenderItem(x, i)
								) : (
									<TextInput
										autoComplete='off'
										className={css(styleSheet.listItemTextField)}
										onChange={this.onItemValueChanged(i)}
										type='text'
										value={value || ''}
										disabled={!canRemove}
									/>
								)}
							</div>
						);
					})}
					<button className={css(styleSheet.button)} onClick={onAddButtonClicked}>
						<RoundAddIcon />
						{addButtonContent}
					</button>
				</div>
			</TooltipProvider>
		);
	}

	private getNextStateWithProps = (nextProps: IProps<T>) => {
		const nextState: IState<T> = {} as IState<T>;

		if (this.state.items !== nextProps.items) {
			const items = nextProps.items || [];
			nextState.items = items;
		}

		if (Object.keys(nextState).length > 0) {
			return nextState;
		}
	};

	private onItemValueChanged = (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
		const { onItemValueChanged } = this.props;
		if (onItemValueChanged) {
			const item = this.state.items[index];
			onItemValueChanged(item, index, e.target.value);
		}
	};

	private onRemoveButtonClicked = (index: number) => () => {
		const { onRemoveButtonClicked } = this.props;
		const { items } = this.state;
		if (onRemoveButtonClicked) {
			onRemoveButtonClicked(items[index], index);
		} else {
			const nextItems = [...(items || [])];
			nextItems.splice(index, 1);
			this.setState({
				items: nextItems,
			});
		}
	};
}

export const EditableEntityInfoBulletList = observer(_EditableEntityInfoBulletList);
